<template>
  <div class="conversation-list">
    <div v-if="showHeader" class="conversation-list__header">
      <Input
        v-if="showSearch"
        v-model="searchQuery"
        type="search"
        placeholder="Search conversations..."
        leading-icon="mdi:magnify"
        size="sm"
        class="conversation-list__search"
      />
    </div>
    <div class="conversation-list__items">
      <div v-if="isLoading && conversations.length === 0" class="conversation-list__loading">
        <LoadingBars size="sm" />
      </div>

      <!-- Email card layout -->
      <template v-if="type === 'email'">
        <div
          v-for="conversation in conversations"
          :key="conversation.id"
          class="conversation-list__email-card"
          :class="{
            'conversation-list__email-card--selected': selectedConversationId === conversation.id,
            'conversation-list__email-card--unread': !conversation.isRead,
          }"
          @click="handleSelect(conversation)"
        >
          <div class="conversation-list__email-card-header">
            <div
              class="conversation-list__email-status-badge"
              :class="`conversation-list__email-status-badge--${getEmailStatus(conversation)}`"
            >
              <Icon
                :icon="getStatusIcon(conversation)"
                class="conversation-list__email-status-icon"
              />
              <span>{{ getStatusLabel(conversation) }}</span>
            </div>
            <span class="conversation-list__email-card-date">{{
              formatTime(conversation.lastMessageAt)
            }}</span>
          </div>
          <div class="conversation-list__email-card-subject">
            {{ conversation.subject || conversation.lastMessage }}
          </div>
          <div class="conversation-list__email-card-sender">
            <div
              class="conversation-list__email-card-avatar"
              :style="{ backgroundColor: getAvatarColor(conversation) }"
            >
              {{ getInitials(getConversationTitle(conversation)) }}
            </div>
            <div class="conversation-list__email-card-sender-info">
              <span class="conversation-list__email-card-sender-name">{{
                getConversationTitle(conversation)
              }}</span>
              <span
                v-if="conversation.contactEmail"
                class="conversation-list__email-card-sender-email"
                >{{ conversation.contactEmail }}</span
              >
            </div>
          </div>
          <div
            v-if="conversation.lastMessage && conversation.subject"
            class="conversation-list__email-card-preview"
          >
            <Icon icon="mdi:reply" class="conversation-list__email-card-preview-icon" />
            <span>{{ conversation.lastMessage }}</span>
          </div>
          <div
            v-if="conversation.threadCount && conversation.threadCount > 1"
            class="conversation-list__email-card-thread"
          >
            <Icon
              icon="mdi:email-multiple-outline"
              class="conversation-list__email-card-thread-icon"
            />
            <span>Thread &middot; {{ conversation.threadCount }} messages</span>
          </div>
        </div>
      </template>

      <!-- Default list layout for other channels -->
      <template v-else>
        <div
          v-for="conversation in conversations"
          :key="conversation.id"
          class="conversation-list__item"
          :class="{
            'conversation-list__item--selected': selectedConversationId === conversation.id,
            'conversation-list__item--unread': !conversation.isRead,
          }"
          @click="handleSelect(conversation)"
        >
          <div class="conversation-list__avatar">
            <img
              v-if="(conversation as any).avatar"
              :src="(conversation as any).avatar"
              :alt="getConversationTitle(conversation)"
              class="conversation-list__avatar-img"
              @error="
                ($event.target as HTMLImageElement).style.display = 'none';
                (
                  ($event.target as HTMLImageElement).nextElementSibling as HTMLElement
                ).style.display = 'flex';
              "
            />
            <span
              class="conversation-list__avatar-initials"
              :class="{ 'conversation-list__avatar-initials--whatsapp': type === 'whatsapp' }"
              :style="{ display: (conversation as any).avatar ? 'none' : 'flex' }"
            >
              {{ getInitials(getConversationTitle(conversation)) }}
            </span>
          </div>
          <div class="conversation-list__item-body">
            <div class="conversation-list__item-header">
              <div class="conversation-list__item-title-wrapper">
                <span
                  class="conversation-list__item-name"
                  :class="{
                    'conversation-list__item-name--unread':
                      conversation.unreadCount && conversation.unreadCount > 0,
                  }"
                  >{{ getConversationTitle(conversation) }}</span
                >
                <span v-if="conversation.subtitle" class="conversation-list__item-separator"
                  >&bull;</span
                >
                <span v-if="conversation.subtitle" class="conversation-list__item-subtitle">{{
                  conversation.subtitle
                }}</span>
              </div>
              <span
                class="conversation-list__item-time"
                :class="{
                  'conversation-list__item-time--unread':
                    conversation.unreadCount && conversation.unreadCount > 0,
                }"
                >{{ formatTime(conversation.lastMessageAt) }}</span
              >
            </div>
            <div class="conversation-list__item-preview">
              <Icon
                v-if="isOutgoingMessage(conversation)"
                :icon="getDeliveryIcon(conversation)"
                class="conversation-list__delivery-icon"
                :class="{ 'conversation-list__delivery-icon--read': conversation.lastMessageSeen }"
              />
              <span
                class="conversation-list__item-message"
                :class="{
                  'conversation-list__item-message--unread':
                    conversation.unreadCount && conversation.unreadCount > 0,
                }"
                >{{ getLastMessagePreview(conversation) }}</span
              >
              <div class="conversation-list__item-badges">
                <span
                  v-if="conversation.threadCount && conversation.threadCount > 1"
                  class="conversation-list__item-thread-count"
                  :title="`${conversation.threadCount} ${conversation.threadCount === 1 ? 'email' : 'emails'} in thread`"
                >
                  {{ conversation.threadCount }}
                </span>
                <span
                  v-if="conversation.unreadCount && conversation.unreadCount > 0"
                  class="conversation-list__item-badge"
                >
                  {{ conversation.unreadCount }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <div v-if="!isLoading && conversations.length === 0" class="conversation-list__empty">
        <p>No conversations found</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Input } from "@/components/ui";
import { Icon } from "@iconify/vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import { resolveLastMessagePreview } from "@/utils/messageFormatters";
import { ref } from "vue";

interface Conversation {
  id: string;
  title: string;
  subtitle?: string;
  subject?: string;
  lastMessage: string;
  lastMessageAt: Date | string;
  unreadCount?: number;
  isRead?: boolean;
  lastSeen?: string;
  threadCount?: number;
  contactEmail?: string;
  contactName?: string;
  displayName?: string;
  status?: string;
  lastMessageIsSender?: boolean;
  lastMessageSeen?: boolean;
  lastMessageDelivered?: boolean;
  lastMessageDirection?: string | null;
}

interface Props {
  conversations: Conversation[];
  selectedConversationId?: string;
  type?: "email" | "linkedin" | "whatsapp" | "sms";
  isLoading?: boolean;
  showSearch?: boolean;
  showHeader?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  conversations: () => [],
  selectedConversationId: "",
  type: "email",
  isLoading: false,
  showSearch: true,
  showHeader: true,
});

const emit = defineEmits<{
  "conversation-selected": [conversation: Conversation];
}>();

const searchQuery = ref("");

const handleSelect = (conversation: Conversation) => {
  emit("conversation-selected", conversation);
};

const formatTime = (timestamp: Date | string): string => {
  const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
  const now = new Date();

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor((startOfToday.getTime() - startOfDay.getTime()) / 86400000);

  if (diffDays === 0) {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) {
    return date.toLocaleDateString("en-US", { weekday: "long" });
  }
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const getLastMessagePreview = (conversation: Conversation): string =>
  resolveLastMessagePreview(conversation);

const getConversationTitle = (conversation: any): string => {
  if (props.type === "email") {
    return (
      conversation.displayName ||
      conversation.title ||
      conversation.contactName ||
      conversation.contactEmail ||
      "Unknown"
    );
  }

  return conversation.title || conversation.contactName || "Unknown";
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const getAvatarColor = (conversation: any): string => {
  const name = getConversationTitle(conversation);
  const colors = ["#6366f1", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#3b82f6", "#ef4444"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const isOutgoingMessage = (conversation: Conversation): boolean => {
  if (conversation.lastMessageIsSender) return true;
  if (conversation.lastMessageDirection === "outbound") return true;
  return false;
};

const getDeliveryIcon = (conversation: Conversation): string => {
  if (conversation.lastMessageSeen) return "mdi:check-all";
  if (conversation.lastMessageDelivered) return "mdi:check-all";
  return "mdi:check";
};

const getEmailStatus = (conversation: any): string => {
  return conversation.status || "sent";
};

const getStatusIcon = (conversation: any): string => {
  const status = getEmailStatus(conversation);
  switch (status) {
    case "read":
      return "mdi:check-all";
    case "delivered":
      return "mdi:check-all";
    case "sent":
    default:
      return "mdi:check";
  }
};

const getStatusLabel = (conversation: any): string => {
  const status = getEmailStatus(conversation);
  switch (status) {
    case "read":
      return "Read";
    case "delivered":
      return "Delivered";
    case "sent":
    default:
      return "Sent";
  }
};
</script>

<style scoped>
.conversation-list {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  border-right: 1px solid #ececfb;
  width: 300px;
  min-width: 300px;
}

.conversation-list__header {
  display: flex;
  gap: 0.5rem;
  padding: 4px;
  border-bottom: 1px solid #e5e7eb;
}

.conversation-list__search {
  flex: 1;
}

.conversation-list__search :deep(.ui-input__field) {
  height: 32px !important;
  padding: 6px 10px 6px 28px !important;
}

.conversation-list__items {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

/* ===== Email Card Layout ===== */
.conversation-list__email-card {
  padding: 12px;
  margin: 8px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.conversation-list__email-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.conversation-list__email-card--selected {
  border-color: #3e3efe;
  background: #f8f8ff;
  box-shadow: 0 0 0 1px #3e3efe;
}

.conversation-list__email-card--unread {
  border-left: 3px solid #3e3efe;
}

.conversation-list__email-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.conversation-list__email-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.conversation-list__email-status-badge--sent {
  background: #f3f4f6;
  color: #6b7280;
}

.conversation-list__email-status-badge--delivered {
  background: #ecfdf5;
  color: #059669;
}

.conversation-list__email-status-badge--read {
  background: #ecfdf5;
  color: #059669;
}

.conversation-list__email-status-icon {
  font-size: 0.75rem;
}

.conversation-list__email-card-date {
  font-size: 0.6875rem;
  color: #9ca3af;
}

.conversation-list__email-card-subject {
  font-weight: 600;
  font-size: 0.8125rem;
  color: #111827;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.conversation-list__email-card-sender {
  display: flex;
  align-items: center;
  gap: 8px;
}

.conversation-list__email-card-avatar {
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.5625rem;
  font-weight: 700;
  color: #ffffff;
  flex-shrink: 0;
}

.conversation-list__email-card-sender-info {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex: 1;
}

.conversation-list__email-card-sender-name {
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.conversation-list__email-card-sender-email {
  font-size: 0.6875rem;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.conversation-list__email-card-preview {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.conversation-list__email-card-preview-icon {
  font-size: 0.875rem;
  color: #9ca3af;
  flex-shrink: 0;
  margin-top: 1px;
}

.conversation-list__email-card-thread {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: #f3f4f6;
  border-radius: 10px;
  font-size: 0.625rem;
  font-weight: 500;
  color: #6b7280;
  align-self: flex-start;
}

.conversation-list__email-card-thread-icon {
  font-size: 0.75rem;
}

/* ===== Default List Layout ===== */
.conversation-list__avatar {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
}

.conversation-list__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.conversation-list__avatar-initials {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #3e3efe;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 13px;
}

.conversation-list__avatar-initials--whatsapp {
  background: #25d366;
}

.conversation-list__item-body {
  flex: 1;
  min-width: 0;
}

.conversation-list__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s;
  border-bottom: 1px solid #f3f4f6;
}

.conversation-list__item:hover {
  background: #f9fafb;
  cursor: pointer;
}

.conversation-list__item--selected {
  background: #f3f4ff;
  border-left: 3px solid #3e3efe;
}

.conversation-list__item--unread {
  background: #ffffff;
  font-weight: 500;
}

.conversation-list__item--unread .conversation-list__item-name {
  font-weight: 600;
}

.conversation-list__item-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.conversation-list__item-title-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.conversation-list__item-name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
}

.conversation-list__item-separator {
  font-size: 0.75rem;
  color: #d1d5db;
  font-weight: 400;
  flex-shrink: 0;
}

.conversation-list__item-subtitle {
  font-weight: 400;
  font-size: 0.75rem;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.conversation-list__item-time {
  font-size: 0.65rem;
  color: #7b7fa8;
  flex-shrink: 0;
}

.conversation-list__item-preview {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.conversation-list__item-badges {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.conversation-list__item-message {
  font-size: 0.8125rem;
  color: #646464;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.conversation-list__delivery-icon {
  font-size: 16px;
  color: #9ca3af;
  flex-shrink: 0;
}

.conversation-list__delivery-icon--read {
  color: #53bdeb;
}

.conversation-list__item-time--unread {
  color: #25d366;
  font-weight: 600;
}

.conversation-list__item-name--unread {
  font-weight: 700 !important;
}

.conversation-list__item-message--unread {
  font-weight: 500;
  color: #374151;
}

.conversation-list__item-badge {
  flex-shrink: 0;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: #3e3efe;
  color: white;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.conversation-list__item-thread-count {
  background: #e5e7eb;
  color: #6b7280;
  font-size: 0.6rem;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 600;
  white-space: nowrap;
  min-width: 18px;
  text-align: center;
}

.conversation-list__empty {
  padding: 2rem;
  text-align: center;
  color: #7b7fa8;
  font-size: 0.875rem;
}

.conversation-list__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  min-height: 300px;
  color: #6b7280;
  font-size: 0.875rem;
}
</style>
